import { Link } from 'react-router-dom';
import { stats } from '@/mocks/tmtData';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const fadeIn = (visible: boolean, delay: string) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'all 700ms ease-out',
  transitionDelay: delay,
});

export default function AboutPage() {
  const [heroTextRef, heroTextVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [heroImgRef, heroImgVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [fintechRef, fintechVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [missionRef, missionVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const [valuesRef, valuesVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const [statsRef, statsVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const [ctaRef, ctaVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <div className="bg-background-50/95">
      {/* Hero — Introduction */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left Text */}
            <div ref={heroTextRef} className="w-full lg:w-1/2">
              <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4" style={fadeIn(heroTextVisible, '0ms')}>
                / About TMT
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-6" style={fadeIn(heroTextVisible, '100ms')}>
                Your{' '}
                <span className="text-primary-400">Future Digital Bank</span>
              </h1>
              <p className="text-foreground-300 text-base md:text-lg leading-relaxed mb-5 font-body" style={fadeIn(heroTextVisible, '200ms')}>
                Trusted Money Transfer (TMT) is a{' '}
                <strong className="text-foreground-100 font-semibold">
                  next-generation digital wallet and payment ecosystem
                </strong>{' '}
                unlike any other, designed to reshape the future of money. It operates in full
                regulatory compliance with{' '}
                <strong className="text-foreground-100 font-semibold">
                  central banking authorities across the Middle East, Africa, and Europe
                </strong>.
              </p>
              <p className="text-foreground-300 text-base leading-relaxed mb-5 font-body" style={fadeIn(heroTextVisible, '300ms')}>
                In today&rsquo;s financial world, regulations across the{' '}
                <strong className="text-foreground-100 font-semibold">
                  Middle East, Africa, and Europe
                </strong>{' '}
                are driving the shift toward{' '}
                <strong className="text-foreground-100 font-semibold">cashless economies</strong>.
                TMT is designed to support this transformation by replacing the need for physical
                cash with a digital solution that gives users complete control of their money directly
                from their mobile phones.
              </p>
              <p className="text-foreground-300 text-base leading-relaxed font-body" style={fadeIn(heroTextVisible, '400ms')}>
                Through{' '}
                <strong className="text-foreground-100 font-semibold">
                  simplicity, security, and inclusivity
                </strong>
                , TMT enables individuals and businesses to manage their entire financial lives
                anytime, anywhere — supported by smart physical service centers available{' '}
                <strong className="text-foreground-100 font-semibold">24/7</strong> for customer
                assistance when needed.
              </p>
            </div>
            {/* Right Image */}
            <div ref={heroImgRef} className="w-full lg:w-1/2">
              <div className="relative overflow-hidden rounded-2xl border border-primary-700/20" style={fadeIn(heroImgVisible, '150ms')}>
                <div className="absolute inset-0 bg-gradient-to-tr from-background-50/40 via-transparent to-transparent z-10" />
                <img
                  src="https://readdy.ai/api/search-image?query=Diverse%20professional%20fintech%20team%20collaborating%20in%20modern%20glass%20walled%20office%20warm%20ambient%20lighting%20premium%20corporate%20environment%20authentic%20teamwork%20moment%20Middle%20East%20and%20Africa%20business%20leaders%20polished%20editorial%20photography%20style%20with%20golden%20accents&width=1000&height=1200&seq=about-hero-team-2&orientation=portrait"
                  alt="TMT Professional Team — Diverse Fintech Leaders"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fintech Visual */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
        <div ref={fintechRef} className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl" style={fadeIn(fintechVisible, '100ms')}>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent z-10" />
            <img
              src="https://public.readdy.ai/ai/img_res/edited_b0176f90c67dcabcabe5f7976b24a331_f4b10401.jpg"
              alt="TMT Future Digital Bank — Next-Generation Fintech Ecosystem"
              className="w-full object-cover rounded-2xl"
            />
          </div>
        </div>
      </section>

      {/* Mission & Vision Teaser */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div ref={missionRef} className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4" style={fadeIn(missionVisible, '0ms')}>
            / What We Stand For
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-6" style={fadeIn(missionVisible, '100ms')}>
            Our Mission & Vision
          </h2>
          <p className="text-foreground-300 text-base leading-relaxed mb-8 font-body max-w-2xl mx-auto" style={fadeIn(missionVisible, '200ms')}>
            At TMT, we are driven by a singular purpose: to democratize financial access and build
            a world where everyone has the power of a bank in their pocket. Our mission and vision
            shape every product we build and every partnership we form.
          </p>
          <div style={fadeIn(missionVisible, '300ms')}>
            <Link
              to="/mission-vision"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-primary-500 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500 hover:text-background-50 transition-all duration-300 whitespace-nowrap"
            >
              Explore Our Mission & Vision
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / What Drives Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              Our Core Values
            </h2>
          </div>

          {/* Values Banner Image */}
          <div className="relative overflow-hidden rounded-2xl mb-12 border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/60 via-transparent to-background-50/60 z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Close%20up%20of%20professional%20hands%20holding%20smartphone%20displaying%20digital%20wallet%20interface%20with%20golden%20payment%20confirmation%20elegant%20business%20setting%20with%20soft%20bokeh%20background%20warm%20lighting%20fintech%20application%20in%20use%20premium%20lifestyle%20photography&width=1400&height=500&seq=about-values-bnr-3&orientation=landscape"
              alt="TMT Digital Wallet — Fintech Excellence in Action"
              className="w-full object-cover"
            />
          </div>

          <div ref={valuesRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-shield-star-line', title: 'Trust', desc: 'Every transaction is built on unwavering trust and complete transparency.' },
              { icon: 'ri-flashlight-line', title: 'Speed', desc: 'We believe financial services should move at the speed of modern life.' },
              { icon: 'ri-global-line', title: 'Accessibility', desc: 'Financial empowerment for everyone, everywhere in the region and beyond.' },
              { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'Pioneering AI and digital solutions that redefine the financial landscape.' },
              { icon: 'ri-hand-heart-line', title: 'Care', desc: 'Our customers are at the heart of every decision, every feature, every interaction.' },
              { icon: 'ri-lock-unlock-line', title: 'Security', desc: 'Bank-grade protection for every user, every transaction, every single day.' },
            ].map((value, i) => (
              <div
                key={value.title}
                className="p-6 md:p-8 bg-background-100/90 border border-primary-800/20 rounded-xl text-center hover:border-primary-600/40 transition-all duration-500"
                style={fadeIn(valuesVisible, `${i * 100}ms`)}
              >
                <div className="w-14 h-14 mx-auto flex items-center justify-center border border-primary-600/30 rounded-full mb-4">
                  <i className={`${value.icon} text-primary-400 text-2xl`} />
                </div>
                <h3 className="font-heading text-xl text-foreground-100 mb-2">{value.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Stats */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Our Impact
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              Numbers That Speak
            </h2>
          </div>
          <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className="p-6 md:p-8 bg-background-50/90 border border-primary-800/20 rounded-xl text-center hover:border-primary-600/40 transition-all duration-500"
                style={fadeIn(statsVisible, `${i * 120}ms`)}
              >
                <p className="font-heading text-3xl md:text-4xl font-bold text-primary-400 mb-2">
                  {stat.value}
                </p>
                <p className="text-foreground-400 text-sm font-body">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div ref={ctaRef} className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-400 mb-4" style={fadeIn(ctaVisible, '0ms')}>
            Join the TMT Family
          </h2>
          <p className="text-foreground-300 text-base leading-relaxed mb-8 font-body" style={fadeIn(ctaVisible, '150ms')}>
            Be part of the financial revolution. Open your account today and experience the future
            of digital payments — TMT: Your Future Digital Bank.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={fadeIn(ctaVisible, '300ms')}>
            <Link
              to="/signup"
              className="px-8 py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Open Account
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3.5 border border-primary-600/50 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}